import { Button, Grid, Theme, Typography, useTheme } from '@mui/material';
import React, { Fragment } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useAppSelector } from '../../../app/hooks/hooks';
import { getPartnerId, getServicingOpCo } from '../../../app/store/combinedSelectors';
import { goToClaimsBtn } from '../../../Constants/Constants';
import { PostMessageCodes } from '../../../Constants/PostMessageCodes';
import { IconStateType, OpCo, PartnerId } from '../../../types/type';
import { postMessageToParentApp } from '../../../utils/Utilities';
import ConfirmationHeader from '../ConfirmationHeader/ConfirmationHeader';
import ModalLinearActivityIndicator from '../ModalLinearActivityIndicator/ModalLinearActivityIndicator';

export enum SERVICE_TYPE {
  REPAIR_SHOP = 1,
  RENTAL_AGENCY = 2,
  VENDOR = 3,
}

export interface ServiceErrorProps {
  serviceType?: SERVICE_TYPE;
}

export default function ServiceError(props: ServiceErrorProps) {
  const theme: Theme = useTheme();
  const navigate = useNavigate();
  const [modalLoadingOpen, setModalLoadingOpen] = React.useState(false);
  const claimSummary = useAppSelector((state) => state.claimDetail.claimSummary);
  const servicingOpco = useSelector(getServicingOpCo);
  const partnerId = useSelector(getPartnerId);

  const getServiceType = () => {
    switch (props.serviceType) {
      case SERVICE_TYPE.REPAIR_SHOP:
        return 'repair shop';
      case SERVICE_TYPE.RENTAL_AGENCY:
        return 'rental shop';
      default:
        return 'vendor';
    }
  };

  const getNextStepMessage = () => {
    let serviceTypeStr = 'vendor';
    switch (props.serviceType) {
      case SERVICE_TYPE.REPAIR_SHOP:
        serviceTypeStr = 'shop';
        break;
      case SERVICE_TYPE.RENTAL_AGENCY:
        serviceTypeStr = 'shop';
        break;
    }

    return <Fragment>To select a {serviceTypeStr}, try again in a few minutes or contact us for assistance.</Fragment>;
  };

  async function goToClaimsOverview() {
    if (partnerId?.toUpperCase() === PartnerId.AFICS) {
      setModalLoadingOpen(true);
      postMessageToParentApp(
        PostMessageCodes.DIGITAL_CLAIM_POST_FNOL_GO_TO_CLAIMS,
        undefined,
        undefined,
        claimSummary?.policyNumber
      );
    } else if (servicingOpco === OpCo.HOMESITE || servicingOpco === OpCo.AMFAM) {
      navigate('/claims-overview', {
        state: {
          isClaimRefreshNeeded: true,
        },
      });
    } else {
      navigate('/claim-detail', {
        state: {
          isClaimDetailRefreshNeeded: true,
        },
      });
    }
  }
  return (
    <div>
      <Grid
        container
        sx={{
          width: '100%',
          maxWidth: '1280px',
          minWidth: '310px',
          margin: '0 auto',
        }}
      >
        <Grid item xs={12}>
          <ConfirmationHeader
            headerContent={`We're having trouble confirming your ${getServiceType()} selection.`}
            state={IconStateType.FAIL_STATE}
          ></ConfirmationHeader>
        </Grid>
        <Grid item xs={12}>
          <Typography
            sx={{
              ...theme.custom.styles.confirmationSubHeader,
              marginTop: '1rem',
            }}
          >
            {'Next Steps'}
          </Typography>
          <Typography sx={{ marginTop: '20px' }} variant="body1">
            {getNextStepMessage()}
          </Typography>
        </Grid>
        <Grid item sm={6} xs={12} sx={{ marginTop: '2rem' }}>
          <Button
            onClick={goToClaimsOverview}
            sx={{
              ...theme.custom.styles.ctaNextButton,
              '&.MuiButtonBase-root:hover': {
                bgcolor: theme.custom.colors.primaryButtonHoverColor,
              },
            }}
          >
            {goToClaimsBtn}
          </Button>
        </Grid>
      </Grid>
      {modalLoadingOpen && <ModalLinearActivityIndicator />}
    </div>
  );
}
